<template xmlns="http://www.w3.org/1999/html">
    <div class="animated fadeIn">
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-header">
                        <i class="fa fa-align-justify"></i> Project Graph
                    </div>
                    <div class="card-block">
                        <div>
                            <button @click="drawGraph" class="float-right">Draw chart</button>
                            <div class="rawDataTable">
                                <table v-if="graphRawData" class="text-right table">
                                    <tr>
                                        <td>Week</td>
                                        <td :class="getRawDataClass(index)" v-for="(week, index) in graphRawData.weeks">{{week}}</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Profit
                                        </td>
                                        <td :class="getRawDataClass(index)" v-for="(balance, index) in graphRawData.balance">{{Math.round(balance)}}</td>
                                    </tr>
                                </table>
                            </div>
                            <div id="chart"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import { mapGetters, mapActions, mapState } from 'vuex';
import { GoogleCharts } from 'google-charts';
import moment from 'moment';

export default {
  name: 'ProjectGraph',
  props: {
    projectId: {
      default: null
    },
    startDate: {
      default: function () { return new Date(new Date().getFullYear(), 0, 1); },
      type: Date
    },
    endDate: {
      default: function () { return new Date(new Date().getFullYear(), 11, 31); },
      type: Date,
    }
  },
  data: function () {
    return {
      options: {
        seriesType: 'bars',
        series: {
          0: {
            type: 'line'
          },
        },
        hAxis: {
          textStyle: {
            fontSize: 11,
          },
        },
        annotations: {
          style: 'line',
        },
        isStacked: true,
        height: 500,
        chartArea: {
          left: 50,
          top: 20,
          width: '80%',
        },
      },
      graphRawData: null,
    };
  },
  watch: {
    graphData: function (val) {
      this.drawGraph();
    },
  },
  computed: {
    ...mapGetters('ProjectFinances', ['labourCostsByProjectByYearweek',
      'plannedLabourCostsByProjectByYearweek',
      'labourCostsByYearweek',
      'materialCostsByProjectByYearweek',
      'materialCostsByYearweek',
      'plannedLabourCostsByYearweek']),
    ...mapGetters('Clients', ['invoicesByProjectByYearweek', 'invoicesByYearweek']),
    ...mapGetters('JiraProjects', ['projects']),
    ...mapGetters('ProjectMaterials', ['projectMaterialCosts']),
    ...mapGetters('IncomingPayments', ['plannedIncomingPaymentsByProjectByYearweek', 'plannedIncomingPaymentsByYearweek']),
    graphRange: function () {
      const range = {
        min: this.getMonday(this.startDate), max: this.getMonday(this.endDate)
      };
      return range;
    },
  },
  methods: {
    ...mapActions('ProjectFinances', ['LOAD_PROJECT_INDEPTH_DATA']),
    ...mapActions('ProjectMaterials', ['LOAD_PROJECT_MATERIALS']),
    ...mapActions('Clients', ['GET_INVOICES_LIST']),
    ...mapActions('IncomingPayments', ['LOAD_INCOMING_PAYMENTS']),
    onGoogleChartsLoaded() {
      this.gChartsLoaded = true;
      this.drawGraph;
    },
    drawGraph() {
      const _this = this;
      if (!window.googleChartsLoaded) {
        window.addEventListener('google-charts-loaded', () => {
          _this.drawGraph();
        });
      } else {
        const chart = new GoogleCharts.api.visualization.ComboChart(document.getElementById('chart'));
        const graphData = this.graphData();
        this.graphRawData = graphData.raw;
        chart.draw(graphData.dataTable, this.options);
      }
    },
    getMonday(d) {
      d = new Date(d);
      const day = d.getDay();
      const diff = d.getDate() - day + (day == 0 ? -6 : 1);
      return new Date(d.setDate(diff));
    },
    getRawDataClass(index) {
      const curweek = moment().format('GGGGWW');
      const itemWeek = this.graphRawData.weeks[index];
      if (itemWeek == curweek) return 'matching';
      return itemWeek < curweek ? 'earlier' : 'later';
    },
    graphData() {
      if (!window.googleChartsLoaded) return;
      const graphData = new GoogleCharts.api.visualization.DataTable();
      const raw = {
        weeks: [],
        balance: []
      };
      // Add columns
      graphData.addColumn('string', 'week');
      graphData.addColumn({ type: 'string', role: 'annotation' });
      graphData.addColumn('number', 'Balance');
      graphData.addColumn('number', 'Labour');
      graphData.addColumn('number', 'Planned labour');
      graphData.addColumn('number', 'Materials');
      graphData.addColumn('number', 'Planned payments');
      graphData.addColumn('number', 'Overdue payments');
      graphData.addColumn('number', 'Paid invoices');
      graphData.addColumn('number', 'Unpaid invoices');
      graphData.addColumn('number', 'Overdue invoices');
      // Get graph range
      const currentDate = new Date(this.graphRange.min);
      // Get resources
      const labourCosts = this.projectId ? this.labourCostsByProjectByYearweek[this.projectId] || {} : this.labourCostsByYearweek;
      const plannedLabourCosts = this.projectId ? this.plannedLabourCostsByProjectByYearweek[this.projectId] || {} : this.plannedLabourCostsByYearweek;
      const materialCosts = this.projectId ? this.materialCostsByProjectByYearweek[this.projectId] || {} : this.materialCostsByYearweek;
      const plannedIncomingPayments = this.projectId ? this.plannedIncomingPaymentsByProjectByYearweek[this.projectId] || {} : this.plannedIncomingPaymentsByYearweek;
      const invoices = this.projectId ? this.invoicesByProjectByYearweek[this.projectId] || {} : this.invoicesByYearweek;
      let balance = 0;

      while (currentDate <= new Date(this.graphRange.max)) {
        const yearweek = moment(currentDate).format('GGGGWW');
        const labour = labourCosts[yearweek] / 100 || 0;
        const plannedLabour = (new Date() <= new Date(currentDate) ? plannedLabourCosts[yearweek] / 100 : 0) || 0;
        const materials = materialCosts[yearweek] / 100 || 0;
        const plannedPayments = plannedIncomingPayments[yearweek] ? plannedIncomingPayments[yearweek].planned : 0;
        const overduePayments = plannedIncomingPayments[yearweek] ? plannedIncomingPayments[yearweek].overdue : 0;
        const paidInvoices = invoices[yearweek] ? invoices[yearweek].received / 100 : 0;
        const unpaidInvoices = invoices[yearweek] ? invoices[yearweek].planned / 100 : 0;
        const overdueInvoices = invoices[yearweek] ? invoices[yearweek].overdue / 100 : 0;
        const income = plannedPayments + overduePayments + paidInvoices + unpaidInvoices + overdueInvoices;
        const expenses = labour + plannedLabour + materials;
        balance += income - expenses;

        // Add to table
        graphData.addRow([
          moment(currentDate).format('MMM D, YY'),
          yearweek == moment(new Date()).format('GGGGWW') ? 'Today' : null,
          balance,
          -labour || null,
          -plannedLabour || null,
          -materials || null,
          plannedPayments || null,
          overduePayments || null,
          paidInvoices || null,
          unpaidInvoices || null,
          overdueInvoices || null
        ]);
        raw.weeks.push(moment(currentDate).format('GGGGWW'));
        raw.balance.push(balance);
        currentDate.setDate(currentDate.getDate() + 7);
      }
      return {
        dataTable: graphData,
        raw: raw
      };
    },
  },
  mounted() {
    this.LOAD_PROJECT_INDEPTH_DATA();
    this.LOAD_PROJECT_MATERIALS();
    this.GET_INVOICES_LIST();
    this.LOAD_INCOMING_PAYMENTS();
  },
  created() {

  }
};
</script>
<style lang="scss" scoped>
    .action {
        margin-left: 5px;
        &:hover {
            cursor: pointer;
            color: #ee0044;
        }
    }
    .rawDataTable {
        width: 100%;
        height: 59px;
        overflow-x: scroll;
        position: relative;
    }
    .rawDataTable table {
        position: absolute;
        left: 0;
        right: 0;
        margin-bottom: 0;
    }
    .rawDataTable table td {
        padding: 0;
        padding-left: 10px;
    }
    .matching {
        background-color: #e0e0ff;
    }
    .earlier {
        background-color: #ffe0e0;
    }
    .later {
        background-color: #e0ffe0;
    }
</style>
